import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";
import { FiRepeat, FiCheckCircle, FiTruck, FiAlertTriangle } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip from "../../../Common/StatusChip";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import { INTER_SITE_TRANSFER_DATA } from "./storeReportsData";

const InterSiteTransferReport = () => {
  const gridRef = useRef(null);
  const [data] = useState(INTER_SITE_TRANSFER_DATA);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalTransfers = data.length;
    const closedCount = data.filter((r) => r.status === "Received & Closed").length;
    const inTransitCount = data.filter((r) => r.status === "Dispatched / In-Transit").length;
    const discrepancyCount = data.filter((r) => r.status === "Discrepancy / Shortage").length;

    return {
      totalTransfers,
      closedCount,
      inTransitCount,
      discrepancyCount,
    };
  }, [data]);

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Transfer ID",
        field: "transferId",
        colId: "transferId",
        flex: 1.2,
        minWidth: 140,
        cellRenderer: (p) => (
          <span
            style={{
              fontWeight: 700,
              color: "var(--color-primary, #0b6462)",
              fontFamily: "monospace",
              fontSize: "12px",
            }}
          >
            {p.value}
          </span>
        ),
      },
      {
        headerName: "Dispatch Date",
        field: "dispatchDate",
        colId: "dispatchDate",
        flex: 1.1,
        minWidth: 125,
        valueFormatter: (p) => (p.value ? moment(p.value).format("DD-MMM-YYYY") : "—"),
      },
      {
        headerName: "Dispatching Site Store",
        field: "dispatchStore",
        colId: "dispatchStore",
        flex: 1.5,
        minWidth: 180,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#1e293b" }}>{p.value}</span>,
      },
      {
        headerName: "Receiving Destination Store",
        field: "receivingStore",
        colId: "receivingStore",
        flex: 1.5,
        minWidth: 180,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#0284c7" }}>{p.value}</span>,
      },
      {
        headerName: "Material Detail & Dispatched Qty",
        field: "materialDetail",
        colId: "materialDetail",
        flex: 2,
        minWidth: 230,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#0f172a" }}>{p.value}</span>,
      },
      {
        headerName: "Delivery Challan / Gate Pass Ref",
        field: "gatePassRef",
        colId: "gatePassRef",
        flex: 1.8,
        minWidth: 200,
        cellRenderer: (p) => (
          <span
            style={{
              fontFamily: "monospace",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              padding: "1px 6px",
              borderRadius: "4px",
              fontSize: "11px",
              color: "#475569",
            }}
          >
            {p.value}
          </span>
        ),
      },
      {
        headerName: "Transfer Status",
        field: "status",
        colId: "status",
        flex: 1.4,
        minWidth: 170,
        cellRenderer: (p) => {
          const val = p.value || "Dispatched / In-Transit";
          let type = "info";
          if (val === "Received & Closed") {
            type = "success";
          } else if (val === "Discrepancy / Shortage") {
            type = "danger";
          }
          return <StatusChip status={val} type={type} />;
        },
      },
    ];
  }, []);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    [],
  );

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const q = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (item.transferId && item.transferId.toLowerCase().includes(q)) ||
        (item.dispatchStore && item.dispatchStore.toLowerCase().includes(q)) ||
        (item.receivingStore && item.receivingStore.toLowerCase().includes(q)) ||
        (item.materialDetail && item.materialDetail.toLowerCase().includes(q)) ||
        (item.gatePassRef && item.gatePassRef.toLowerCase().includes(q));

      const matchesStatus =
        statusFilter === "all" || item.status?.toLowerCase() === statusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [data, searchTerm, statusFilter]);

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Inter_Site_Store_Transfers_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("Inter-site transfers report exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL INTER-SITE TRANSFERS"
            value={`${kpis.totalTransfers} Dispatches`}
            subtitle="Movements between sites"
            icon={FiRepeat}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="RECEIVED & RECONCILED"
            value={`${kpis.closedCount} Completed`}
            subtitle="Stock taken on site ledger"
            icon={FiCheckCircle}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="ACTIVE IN-TRANSIT"
            value={`${kpis.inTransitCount} En-Route`}
            subtitle="Carrier / truck on highway"
            icon={FiTruck}
            color="#0284c7"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="SHORTAGE / DISCREPANCY"
            value={`${kpis.discrepancyCount} Investigation`}
            subtitle="Transit damage or missing"
            icon={FiAlertTriangle}
            color="#dc2626"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (General Journal style) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="Inter-Site Transfers"
            >
              Inter-Site Transfers
            </div>
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search transfer ID, store, item..."
            />

            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            <div className="d-flex align-items-center" style={{ maxWidth: "240px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Transfer Statuses</option>
                  <option value="dispatched / in-transit">Dispatched / In-Transit</option>
                  <option value="received & closed">Received & Closed</option>
                  <option value="discrepancy / shortage">Discrepancy / Shortage</option>
                </select>
                <label>Transfer Status</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterSiteTransferReport;
